import React, { useContext, useState } from 'react';
import axios from 'axios';
import { Button, Dropdown, Menu, message } from 'antd';
import {
  CheckSquareOutlined,
  CloseOutlined,
  DownloadOutlined,
  SendOutlined,
  DownOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import ENVIRONMENT from '../../../../environments';
import TwoDImagesContext from '../../ContextFiles/TwoDImagesContext';
import BulkTaskContext from '../../ContextFiles/BulkTaskContext';

const SelectionControls = () => {
  const {
    selectionMode,
    selectedImageIds,
    enterSelectionMode,
    exitSelectionMode,
    selectAllImages,
    deselectAllImages,
    getSelectedImages,
    filteredImagesCount,
  } = useContext(TwoDImagesContext);

  const { addTask } = useContext(BulkTaskContext);
  const [downloading, setDownloading] = useState(false);
  const [submittingForReview, setSubmittingForReview] = useState(false);

  const selectedCount = selectedImageIds.size;
  const allSelected = selectedCount > 0 && filteredImagesCount > 0 && selectedCount >= filteredImagesCount;

  const MAX_BULK_DOWNLOAD = 500;
  const MAX_BULK_SUBMIT_REVIEW = 500;

  const handleBulkDownload = async (format = 'jpg') => {
    const images = getSelectedImages();
    if (images.length === 0) return;
    if (images.length > MAX_BULK_DOWNLOAD) {
      message.warning(`You can download up to ${MAX_BULK_DOWNLOAD} images at a time. Please deselect some images.`);
      return;
    }
    const imageIds = images.map((img) => Number(img.id));
    const companyId = localStorage.getItem('company_id');
    const username = localStorage.getItem('username');
    const imageCount = images.length;

    setDownloading(true);
    try {
      const res = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, {
        action: 'trigger_bulk_action_download',
        image_ids: imageIds,
        format,
        company_id: Number(companyId),
        created_by: username,
      });

      const bulkActionId = res.data?.bulk_action_id;
      if (!bulkActionId) {
        message.error('Failed to initiate bulk download — no action ID returned.');
        return;
      }

      // Add task to the global panel — polling starts automatically
      addTask({
        title: 'Preparing download',
        label: `Zipping ${imageCount} file${imageCount !== 1 ? 's' : ''}`,
        imageCount,
        bulkActionId,
      });

      message.info('Download queued. You can track progress in the panel below.');
      exitSelectionMode();
    } catch (error) {
      console.error('Bulk download error:', error);
      message.error('Failed to initiate bulk download.');
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkSubmitForReview = async () => {
    const images = getSelectedImages();
    // Only images — videos use a different action and aren't supported in bulk
    const imageOnly = images.filter((img) => img.image_type !== 'ai_video');

    if (imageOnly.length === 0) {
      message.warning('No images selected. Videos cannot be submitted for review in bulk.');
      return;
    }
    if (imageOnly.length > MAX_BULK_SUBMIT_REVIEW) {
      message.warning(`You can submit up to ${MAX_BULK_SUBMIT_REVIEW} images at a time. Please deselect some images.`);
      return;
    }

    const imageIds = imageOnly.map((img) => Number(img.id));
    const username = localStorage.getItem('username');

    setSubmittingForReview(true);
    const hideLoading = message.loading('Submitting images for review...', 0);
    try {
      const res = await axios.post(ENVIRONMENT.REVIEW_IMAGE_CONTROLLER, {
        action: 'bulk_add_review_image',
        image_ids: imageIds,
        submitted_by: username,
      });

      const statusCode = res.data?.statusCode;
      // Body is an object on success but a JSON string on errors — normalize
      let body = res.data?.body;
      if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch (_) { /* leave as string */ }
      }

      if (statusCode && statusCode !== 200) {
        message.error('Something went wrong. Please try again.');
        return;
      }

      const insertCount = body?.data?.insert_batch_count;
      if (insertCount > 0) {
        message.success(`${imageOnly.length} image${imageOnly.length !== 1 ? 's' : ''} submitted for review.`);
      } else {
        message.info('All selected images are already in review.');
      }
      exitSelectionMode();
    } catch (error) {
      console.error('Bulk submit for review error:', error);
      message.error('Something went wrong. Please try again.');
    } finally {
      hideLoading();
      setSubmittingForReview(false);
    }
  };

  if (!selectionMode) {
    return (
      <Button
        icon={<CheckSquareOutlined />}
        onClick={enterSelectionMode}
        style={{
          borderRadius: '6px',
          fontWeight: 500,
          fontSize: '14px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        Select
      </Button>
    );
  }

  const bulkMenuOverlay = (
    <Menu>
      <Menu.Item
        key="download_jpg"
        icon={downloading ? <LoadingOutlined /> : <DownloadOutlined />}
        onClick={() => handleBulkDownload('jpg')}
        disabled={downloading}
      >
        {downloading ? 'Downloading...' : 'Download as JPG'}
      </Menu.Item>
      <Menu.Item
        key="download_png"
        icon={downloading ? <LoadingOutlined /> : <DownloadOutlined />}
        onClick={() => handleBulkDownload('png')}
        disabled={downloading}
      >
        {downloading ? 'Downloading...' : 'Download as PNG'}
      </Menu.Item>
      <Menu.Item
        key="submit_for_review"
        icon={submittingForReview ? <LoadingOutlined /> : <SendOutlined />}
        onClick={handleBulkSubmitForReview}
        disabled={submittingForReview}
      >
        {submittingForReview ? 'Submitting...' : 'Submit for Review'}
      </Menu.Item>
    </Menu>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span className="manrope f-14" style={{ color: '#262626', whiteSpace: 'nowrap', fontWeight: 500 }}>
        <span style={{ fontWeight: 700, color: '#1890ff' }}>{selectedCount}</span> selected
      </span>

      <Button
        type="link"
        onClick={allSelected ? deselectAllImages : selectAllImages}
        style={{ padding: '0 4px', fontSize: '13px', fontWeight: 500, height: 'auto' }}
      >
        {allSelected ? 'Deselect All' : 'Select All'}
      </Button>

      <Dropdown
        overlay={bulkMenuOverlay}
        trigger={['click']}
        disabled={selectedCount === 0}
      >
        <Button
          disabled={selectedCount === 0}
          style={{
            borderRadius: '6px',
            fontWeight: 500,
            fontSize: '13px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          Actions <DownOutlined style={{ fontSize: '10px' }} />
        </Button>
      </Dropdown>

      <CloseOutlined
        onClick={exitSelectionMode}
        style={{ fontSize: '14px', color: '#8c8c8c', cursor: 'pointer', padding: '4px' }}
      />
    </div>
  );
};

export default SelectionControls;
